import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { DollarSign, ArrowUpRight, ArrowDownLeft, Plus, CheckCircle2, Clock, AlertTriangle, Trash2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function Settlements({ user }) {
  const [settlements, setSettlements] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ type: 'receivable', entity_name: '', amount: '', description: '', due_date: '', load_id: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = () => {
    setLoading(true);
    Promise.all([api.get('/settlements'), api.get('/settlements/summary')])
      .then(([sRes, sumRes]) => { setSettlements(sRes.data); setSummary(sumRes.data); })
      .catch(() => toast.error('Failed to load settlements'))
      .finally(() => setLoading(false));
  };

  const set = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.entity_name || !form.amount) { toast.error('Entity name and amount are required'); return; }
    try {
      await api.post('/settlements', { ...form, amount: parseFloat(form.amount) });
      toast.success('Settlement created');
      setShowForm(false);
      setForm({ type: 'receivable', entity_name: '', amount: '', description: '', due_date: '', load_id: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const markPaid = async (id) => {
    try { await api.put(`/settlements/${id}`, { status: 'paid' }); toast.success('Marked as paid'); loadData(); }
    catch { toast.error('Failed to update'); }
  };

  const deleteSettlement = async (id) => {
    if (!window.confirm('Delete this settlement?')) return;
    try { await api.delete(`/settlements/${id}`); toast.success('Deleted'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const exportXLSX = () => {
    const rows = settlements.map(s => ({
      Type: s.type === 'receivable' ? 'Receivable (AR)' : 'Payable (AP)',
      Entity: s.entity_name, Amount: s.amount, Status: s.status,
      'Due Date': s.due_date || '', 'Paid Date': s.paid_date || '', Description: s.description,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Settlements');
    XLSX.writeFile(wb, 'settlements.xlsx');
  };

  const filtered = filter === 'all' ? settlements : settlements.filter(s => filter === 'receivable' ? s.type === 'receivable' : filter === 'payable' ? s.type === 'payable' : s.status === filter);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="settlements-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Settlements & AR/AP</h1>
          <p className="text-slate-600 mt-1">Track receivables, payables, and settlements</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportXLSX} data-testid="export-settlements-btn"><Download className="w-4 h-4 mr-1" /> Export</Button>
          <Button onClick={() => setShowForm(true)} data-testid="add-settlement-btn"><Plus className="w-4 h-4 mr-2" /> Add Settlement</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-receivable"><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-green-100"><ArrowDownLeft className="w-5 h-5 text-green-600" /></div><div><p className="text-sm text-slate-500">Pending Receivable</p><p className="text-2xl font-bold text-green-700">${(summary.total_receivable || 0).toFixed(2)}</p></div></div></CardContent></Card>
        <Card data-testid="stat-payable"><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-red-100"><ArrowUpRight className="w-5 h-5 text-red-600" /></div><div><p className="text-sm text-slate-500">Pending Payable</p><p className="text-2xl font-bold text-red-700">${(summary.total_payable || 0).toFixed(2)}</p></div></div></CardContent></Card>
        <Card data-testid="stat-collected"><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-blue-100"><CheckCircle2 className="w-5 h-5 text-blue-600" /></div><div><p className="text-sm text-slate-500">Total Collected</p><p className="text-2xl font-bold text-blue-700">${(summary.total_collected || 0).toFixed(2)}</p></div></div></CardContent></Card>
        <Card data-testid="stat-net"><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-purple-100"><DollarSign className="w-5 h-5 text-purple-600" /></div><div><p className="text-sm text-slate-500">Net Position</p><p className={`text-2xl font-bold ${(summary.net_position || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>${(summary.net_position || 0).toFixed(2)}</p></div></div></CardContent></Card>
      </div>

      {/* Filter */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[['all','All'],['receivable','Receivable'],['payable','Payable'],['pending','Pending'],['paid','Paid']].map(([k,l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`filter-${k}`}>{l}</button>
        ))}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">No settlements found. Add your first settlement.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(s => (
            <Card key={s.id} data-testid={`settlement-${s.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${s.type === 'receivable' ? 'bg-green-100' : 'bg-red-100'}`}>
                    {s.type === 'receivable' ? <ArrowDownLeft className="w-4 h-4 text-green-600" /> : <ArrowUpRight className="w-4 h-4 text-red-600" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900">{s.entity_name}</p>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.type === 'receivable' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{s.type === 'receivable' ? 'AR' : 'AP'}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.status === 'paid' ? 'bg-blue-100 text-blue-700' : s.status === 'overdue' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{s.status}</span>
                    </div>
                    <p className="text-xs text-slate-500">{s.description}{s.due_date ? ` | Due: ${s.due_date}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <p className={`text-lg font-bold ${s.type === 'receivable' ? 'text-green-700' : 'text-red-700'}`}>${s.amount.toFixed(2)}</p>
                  {s.status === 'pending' && <Button size="sm" variant="outline" onClick={() => markPaid(s.id)} data-testid={`mark-paid-${s.id}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Paid</Button>}
                  {user.role !== 'agent' && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteSettlement(s.id)} data-testid={`delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Settlement</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="receivable">Receivable (Broker owes us)</SelectItem><SelectItem value="payable">Payable (We owe carrier)</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Entity Name *</Label><Input required value={form.entity_name} onChange={set('entity_name')} placeholder="Company or person name" data-testid="form-entity" /></div>
            <div><Label>Amount *</Label><Input required type="number" step="0.01" value={form.amount} onChange={set('amount')} placeholder="0.00" data-testid="form-amount" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={set('description')} placeholder="What is this for?" data-testid="form-description" /></div>
            <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={set('due_date')} data-testid="form-due-date" /></div>
            <div><Label>Load ID (optional)</Label><Input value={form.load_id} onChange={set('load_id')} placeholder="Link to a load" data-testid="form-load-id" /></div>
            <Button type="submit" className="w-full" data-testid="save-settlement-btn">Add Settlement</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
